import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import allServices, { getServicesByCategory } from "@/data/services";

const coreServices = getServicesByCategory("core");
const emergingServices = getServicesByCategory("emerging");
const lifecycleServices = getServicesByCategory("lifecycle");

function ServiceCard({ s, i }) {
  const isOdd = i % 2 === 1;
  return (
    <div data-testid={`service-detail-${i}`} className="grid grid-cols-1 lg:grid-cols-2 items-stretch rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm hover:shadow-xl hover:shadow-blue-500/8 transition-all duration-500">
      <div className={`relative overflow-hidden ${isOdd ? "lg:order-2" : ""}`}>
        <img src={s.image} alt={s.title} className="w-full h-full min-h-[300px] object-cover" />
        {s.tag && <span className="absolute top-4 left-4 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white bg-[#0D72B8] rounded-full">{s.tag}</span>}
      </div>
      <div className={`p-10 flex flex-col justify-center ${isOdd ? "lg:order-1" : ""}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#0D72B8] to-[#2B8AD4]">
            <s.icon size={20} className="text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-['Sora'] font-bold text-[#111827]">{s.title}</h2>
        </div>
        <p className="text-[#6B7280] leading-relaxed mb-6 text-sm">{s.shortDesc || s.desc}</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
          {s.features.map((f, j) => (
            <li key={j} className="flex items-start gap-2">
              <CheckCircle size={14} className="text-[#0D72B8] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#6B7280]">{f}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-5">
          <Link to={`/services/${s.slug}`} className="inline-flex items-center gap-2 text-[#0D72B8] text-sm font-semibold hover:gap-3 transition-all">
            Learn more <ArrowRight size={14} />
          </Link>
          <Link to="/contact" className="inline-flex items-center gap-2 text-[#6B7280] text-sm font-medium hover:text-[#0D72B8] transition-colors">
            Enquire now <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  usePageMeta("Services", "Explore Vaana's full suite of services - IT infrastructure, ERP, cloud, cybersecurity, AR/VR, AI/ML, IoT, and AMC/CMC. Each solution is designed for reliability, scalability, and innovation.");
  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 md:py-32 wave-divider overflow-hidden">
        <div className="absolute inset-0 bg-[#0D72B8]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-xs tracking-[0.2em] uppercase text-blue-200 font-semibold mb-4">Our Services</p>
          <h1 className="text-5xl sm:text-6xl leading-tight font-['Sora'] font-medium text-white mb-6">Comprehensive technology solutions for every enterprise need.</h1>
          <p className="text-blue-100 max-w-2xl leading-relaxed">Explore Vaana's full suite of services - IT infrastructure, ERP, cloud, cybersecurity, AR/VR, AI/ML, IoT, and AMC/CMC. Each solution is designed for reliability, scalability, and innovation.</p>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#0D72B8] font-semibold mb-3">Core Services</p>
            <h2 className="text-3xl sm:text-4xl font-['Sora'] font-semibold text-[#111827]">IT, Cloud & Security</h2>
          </div>
          <div className="space-y-8">
            {coreServices.map((s, i) => <ServiceCard key={s.slug} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* Emerging */}
      <section className="py-24 md:py-32 bg-[#F9FAFB] border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#0D72B8] font-semibold mb-3">Emerging Technologies</p>
            <h2 className="text-3xl sm:text-4xl font-['Sora'] font-semibold text-[#111827]">Built for practical deployment</h2>
          </div>
          <div className="space-y-8">
            {emergingServices.map((s, i) => <ServiceCard key={s.slug} s={s} i={i + 6} />)}
          </div>
        </div>
      </section>

      {/* Lifecycle */}
      <section className="py-24 md:py-32 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#0D72B8] font-semibold mb-3">Lifecycle & Operations</p>
            <h2 className="text-3xl sm:text-4xl font-['Sora'] font-semibold text-[#111827]">Support for institutions & operations</h2>
          </div>
          <div className="space-y-8">
            {lifecycleServices.map((s, i) => <ServiceCard key={s.slug} s={s} i={i + 12} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D72B8] py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-['Sora'] font-semibold text-white mb-6">Need a custom solution?</h2>
          <p className="text-blue-100 max-w-xl mx-auto mb-10">Every business is unique. Let us design a tailored technology solution for your requirements.</p>
          <Link to="/contact" data-testid="services-cta-button" className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#0D72B8] font-semibold text-sm rounded-full hover:bg-gray-50 transition-all shadow-lg">
            Request a Consultation <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
